import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/tables/Table";
import { RevellerProfile, UserRole } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { useTableState } from '@/hooks/useTableState';
import { DataTablePagination } from '@/components/tables/DataTablePagination';

const ActionButton: React.FC<{ onClick: () => void; className: string; children: React.ReactNode }> = ({ onClick, className, children }) => (
  <button
    onClick={onClick}
    className={`text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${className}`}>{children}
  </button>
);

const TableActions: React.FC<{ 
  reveller: RevellerProfile;
  role: UserRole; 
  onAction: (action: string, revellerId: string) => void;
}> = ({ reveller, role, onAction }) => {

  const handleAction = (action: string) => {
    onAction(action, reveller.id);
  };

  // Ensure Edit and Delete buttons are shown for admin roles
  if (role === UserRole.ADMIN || role === UserRole.SYSTEM_ADMIN_LIVE) {
    return (
        <div className="space-x-2">
          <ActionButton onClick={() => handleAction('edit')} className="bg-blue-500/20 text-blue-300 hover:bg-blue-500/40">Edit</ActionButton>
          <ActionButton onClick={() => handleAction('delete')} className="bg-red-500/20 text-red-300 hover:bg-red-500/40">Delete</ActionButton>
        </div>
      );
  }
  
  return <></>;
};

const RevellerRealDataTable = ({ currentUserRole }: { currentUserRole: UserRole }) => {
  const [revellers, setRevellers] = useState<RevellerProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { notify } = useToast();

  const {
    pageIndex,
    pageSize,
    setPageIndex,
    setPageSize,
    getPaginatedData,
  } = useTableState<RevellerProfile>(revellers, 10);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // First, try to fetch from the live API
        const response = await fetch('/api/revellers');
        if (response.ok) {
          const data: RevellerProfile[] = await response.json();
          setRevellers(data);
          notify('Successfully loaded real-time reveller data.', 'success');
        } else {
          // If the live API fails, throw an error to fall back to the mock data file
          throw new Error('Live API not available. Trying mock data.');
        }
      } catch {
        // If the live API fetch fails, try to fetch from the mock data file
        try {
          const response = await fetch('/revellers.json');
          if (!response.ok) {
            throw new Error(`Failed to fetch revellers: ${response.statusText}`);
          }
          const data: RevellerProfile[] = await response.json();
          setRevellers(data);
          notify('Could not load real-time data. Displaying mock data instead.', 'warn');
        } catch (error) {
          console.error("API Error:", (error as Error).message);
          notify('Failed to load both live and mock data for revellers.', 'error');
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [notify]);

  const handleAction = (action: string, revellerId: string) => {
    notify(`Action '${action}' triggered for reveller '${revellerId}'.`, 'info');
    // Handle the delete action by filtering the state
    if (action === 'delete') {
      setRevellers(currentRevellers => currentRevellers.filter(r => r.id !== revellerId));
      notify(`Reveller ${revellerId} was successfully deleted.`, 'success');
    }
  };

  if (isLoading) return <div className="text-center p-8 text-kala-400">Loading reveller data...</div>;

  const paginatedRevellers = getPaginatedData();
  const pageCount = Math.ceil(revellers.length / pageSize);

  return (
    <div>
      <Table className="bg-kala-900 text-white rounded-lg p-6">
        <TableCaption>Real-time reveller data. {pageCount > 1 && `(Page ${pageIndex + 1} of ${pageCount})`}</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Tickets Purchased</TableHead>
            <TableHead>Verified</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedRevellers.map((reveller) => (
            <TableRow key={reveller.id}>
              <TableCell>{reveller.user?.name || 'N/A'}</TableCell>
              <TableCell>{reveller.tickets_purchased}</TableCell>
              <TableCell>{reveller.verified ? 'Yes' : 'No'}</TableCell>
              <TableCell className="text-right">
                  <TableActions reveller={reveller} role={currentUserRole} onAction={handleAction} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <DataTablePagination
        pageIndex={pageIndex}
        pageCount={pageCount}
        pageSize={pageSize}
        setPageIndex={setPageIndex}
        setPageSize={setPageSize}
        />
    </div>
  );
};

export default RevellerRealDataTable;
